import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Layers, Lock } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Badge, StatusBadge } from '@/components/common/Badge';
import { ConfirmDialog, Modal } from '@/components/common/Modal';
import { TextField, SelectField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { useResourceActions } from '@/hooks/useResourceActions';
import { useAcademicYearPicker, useCampusPicker, useGradePicker } from '@/hooks/usePickers';
import { gradeOfferingsApi } from '@/services/api/endpoints/academic';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { AcademicStatus, GradeOffering } from '@/types/domain';
import { orDash } from '@/utils/format';

const schema = z.object({
  academicYearId: z.string().uuid('Select an academic year'),
  campusId: z.string().uuid('Select a campus'),
  gradeId: z.string().uuid('Select a grade'),
  capacity: z.union([z.coerce.number().int().min(1).max(5000), z.literal('')]),
});

type FormValues = z.infer<typeof schema>;

/**
 * Which campus runs which grade in which academic year.
 *
 * This is the screen that makes the rest of the structure work: a grade is
 * defined once for the school, and an offering says where and when it actually
 * runs. Sections are then created beneath an offering, which is what keeps a
 * finished year's structure frozen.
 */
export function GradeOfferingsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const table = useTableQuery();

  const [yearFilter, setYearFilter] = useState('');
  const [campusFilter, setCampusFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<AcademicStatus | ''>('');
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<GradeOffering | null>(null);

  const years = useAcademicYearPicker();
  const campuses = useCampusPicker();
  const grades = useGradePicker();

  const params = {
    ...table.params,
    ...(yearFilter !== '' ? { academicYearId: yearFilter } : {}),
    ...(campusFilter !== '' ? { campusId: campusFilter } : {}),
    ...(statusFilter !== '' ? { status: statusFilter } : {}),
  };

  const query = useQuery({
    queryKey: queryKeys.gradeOfferings.list(params),
    queryFn: () => gradeOfferingsApi.list(params),
  });

  const { statusMutation, deleteMutation, invalidateAll } = useResourceActions({
    invalidates: [queryKeys.gradeOfferings.all, queryKeys.grades.all, queryKeys.academicYears.all],
    changeStatus: gradeOfferingsApi.changeStatus,
    remove: gradeOfferingsApi.remove,
    label: 'Grade offering',
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { academicYearId: '', campusId: '', gradeId: '', capacity: '' },
  });

  const create = useMutation({
    mutationFn: (values: FormValues) =>
      gradeOfferingsApi.create({
        academicYearId: values.academicYearId,
        campusId: values.campusId,
        gradeId: values.gradeId,
        ...(values.capacity !== '' ? { capacity: Number(values.capacity) } : {}),
      }),
    onSuccess: () => {
      toast.success('Grade offering created');
      setCreating(false);
      form.reset();
      invalidateAll();
      void queryClient.invalidateQueries({ queryKey: queryKeys.sections.all });
    },
    onError: (error) => handleApiError(error, form.setError),
  });

  const columns: Column<GradeOffering>[] = [
    {
      key: 'grade',
      header: 'Grade',
      render: (offering) => (
        <div className="flex items-center gap-2">
          <span className="font-medium text-ink">{offering.grade.name}</span>
          {offering.historical && (
            <Badge tone="neutral">
              <Lock className="h-3 w-3" aria-hidden="true" />
              read-only
            </Badge>
          )}
        </div>
      ),
    },
    { key: 'campus', header: 'Campus', render: (offering) => orDash(offering.campusName) },
    {
      key: 'year',
      header: 'Academic year',
      render: (offering) => (
        <div className="flex items-center gap-2">
          {offering.academicYear.name}
          {offering.academicYear.current && <Badge tone="accent">current</Badge>}
        </div>
      ),
    },
    {
      key: 'sections',
      header: 'Sections',
      render: (offering) => <span className="tabular-nums">{offering.sectionCount}</span>,
    },
    {
      key: 'capacity',
      header: 'Planned intake',
      sortKey: 'capacity',
      render: (offering) => <span className="tabular-nums">{orDash(offering.capacity)}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      sortKey: 'status',
      render: (offering) => <StatusBadge status={offering.status} />,
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (offering) => (
        <div className="flex justify-end gap-1">
          {/* A finished year is history; nothing beneath it may be touched. */}
          {can(PERMISSIONS.GRADE_OFFERING_UPDATE) && !offering.historical && (
            <Button
              size="sm"
              variant="ghost"
              loading={statusMutation.isPending}
              onClick={() =>
                statusMutation.mutate({
                  id: offering.id,
                  payload: { status: offering.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE' },
                })
              }
            >
              {offering.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
            </Button>
          )}
          {can(PERMISSIONS.GRADE_OFFERING_DELETE) && !offering.historical && (
            <Button size="sm" variant="ghost" onClick={() => setDeleting(offering)}>
              Delete
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <ListPage
        title="Grade Offerings"
        description="Which campus runs which grade, in which academic year. Sections are created beneath an offering."
        icon={<Layers className="h-5 w-5" aria-hidden="true" />}
        action={
          can(PERMISSIONS.GRADE_OFFERING_CREATE) ? (
            <Button onClick={() => setCreating(true)}>Offer a grade</Button>
          ) : undefined
        }
        search={{ value: table.searchInput, onChange: table.setSearchInput, placeholder: 'Search grades…' }}
        filters={
          <>
            <FilterSelect
              label="Year"
              value={yearFilter}
              onChange={(value) => {
                setYearFilter(value);
                table.setPage(0);
              }}
              options={(years.data ?? []).map((year) => ({ value: year.id, label: year.name }))}
            />
            <FilterSelect
              label="Campus"
              value={campusFilter}
              onChange={(value) => {
                setCampusFilter(value);
                table.setPage(0);
              }}
              options={(campuses.data ?? []).map((campus) => ({ value: campus.id, label: campus.name }))}
            />
            <FilterSelect
              label="Status"
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value);
                table.setPage(0);
              }}
              options={[
                { value: 'ACTIVE', label: 'Active' },
                { value: 'INACTIVE', label: 'Inactive' },
              ]}
            />
          </>
        }
        pagination={query.data?.pagination}
        onPageChange={table.setPage}
        onSizeChange={table.setSize}
      >
        <DataTable
          columns={columns}
          rows={query.data?.data}
          rowKey={(offering) => offering.id}
          loading={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          emptyTitle="No grade offerings yet"
          emptyDescription="Offer a grade at a campus for an academic year, then divide it into sections."
          emptyIcon={<Layers className="h-6 w-6" aria-hidden="true" />}
        />
      </ListPage>

      <Modal
        open={creating}
        title="Offer a grade"
        description="Pick the year, campus and grade. The combination must be unique, and the year must not have finished."
        onClose={() => setCreating(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreating(false)}>
              Cancel
            </Button>
            <Button onClick={form.handleSubmit((values) => create.mutate(values))} loading={create.isPending}>
              Create
            </Button>
          </>
        }
      >
        <form
          className="space-y-4"
          noValidate
          onSubmit={form.handleSubmit((values) => create.mutate(values))}
        >
          <SelectField
            label="Academic year"
            required
            error={form.formState.errors.academicYearId?.message}
            {...form.register('academicYearId')}
          >
            <option value="">Select a year…</option>
            {(years.data ?? [])
              // A finished year rejects new structure, so it is not offered.
              .filter((year) => year.status === 'PLANNED' || year.status === 'ACTIVE')
              .map((year) => (
                <option key={year.id} value={year.id}>
                  {year.name}
                  {year.current ? ' (current)' : ''}
                </option>
              ))}
          </SelectField>

          <SelectField
            label="Campus"
            required
            error={form.formState.errors.campusId?.message}
            {...form.register('campusId')}
          >
            <option value="">Select a campus…</option>
            {(campuses.data ?? []).map((campus) => (
              <option key={campus.id} value={campus.id}>
                {campus.name}
              </option>
            ))}
          </SelectField>

          <SelectField
            label="Grade"
            required
            hint="Grades are defined once for the school; this places one at a campus for a year."
            error={form.formState.errors.gradeId?.message}
            {...form.register('gradeId')}
          >
            <option value="">Select a grade…</option>
            {(grades.data ?? [])
              .filter((grade) => grade.status === 'ACTIVE')
              .map((grade) => (
                <option key={grade.id} value={grade.id}>
                  {grade.name}
                </option>
              ))}
          </SelectField>

          <TextField
            label="Planned intake"
            type="number"
            min={1}
            max={5000}
            hint="Optional and advisory. The operative limit is the sum of this offering's sections."
            error={form.formState.errors.capacity?.message}
            {...form.register('capacity')}
          />
        </form>
      </Modal>

      <ConfirmDialog
        open={deleting !== null}
        title="Delete grade offering"
        message={
          deleting === null
            ? ''
            : `Withdraw ${deleting.grade.name} from ${deleting.campusName ?? 'this campus'} for ${deleting.academicYear.name}? This is refused while sections exist beneath it.`
        }
        confirmLabel="Delete"
        destructive
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (deleting !== null) {
            deleteMutation.mutate(deleting.id, { onSettled: () => setDeleting(null) });
          }
        }}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
}
